import React, { useState, useEffect } from 'react';
import { 
  Store, 
  Plus, 
  Search, 
  Edit, 
  Archive,
  MapPin,
  CheckCircle2,
  XCircle,
  Upload,
  Globe,
  Settings,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Percent,
  User,
  Trash2
} from 'lucide-react';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { collection, 
  query, 
  where, 
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs } from '../firebase';
import { notify } from '../services/notify';

const getLocalAuthToken = () => typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
const isLocalSession = () => Boolean(getLocalAuthToken());
const localApiHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getLocalAuthToken() || ''}` });

interface DiscountRule {
  userIds: string[];
  minDiscount: number;
  maxDiscount: number;
}

interface StoreLocation {
  id: string;
  orgId: string;
  name: string;
  address: string;
  status: 'active' | 'inactive' | 'archived';
  termsAndConditions?: string;
  logoUrl?: string;
  socialMediaLink?: string;
  posPrefix?: string;
  nextPosNumber?: number;
  enforceCustomerOnCheckout?: boolean;
  allowedPaymentAccountIds?: string[];
  discountRules?: DiscountRule[];
}

export default function StoresView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [stores, setStores] = useState<StoreLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreLocation | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDiscountSettings, setShowDiscountSettings] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    status: 'active' as 'active' | 'inactive' | 'archived',
    termsAndConditions: '',
    logoUrl: '',
    socialMediaLink: '',
    posPrefix: 'Receipt Number-',
    nextPosNumber: 1,
    enforceCustomerOnCheckout: false,
    allowedPaymentAccountIds: [] as string[],
    discountRules: [] as DiscountRule[]
  });

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers: localApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: localApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/users`, { headers: localApiHeaders() }).then(r => r.json()),
      ]).then(([banks, coa, users]) => {
        if (cancelled) return;
        setBankAccounts(Array.isArray(banks) ? banks : []);
        setChartOfAccounts(Array.isArray(coa) ? coa : []);
        setOrgUsers(Array.isArray(users) ? users : []);
      }).catch(err => { if (!cancelled) console.error('StoresView local data load failed:', err); });
      return () => { cancelled = true; };
    }

    // Fetch bank accounts
    const bankQuery = query(
      collection(db, 'bank_accounts'),
      where('orgId', '==', orgId)
    );
    const unsubscribeBank = onSnapshot(bankQuery, (snapshot) => {
      setBankAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch chart of accounts
    const coaQuery = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId)
    );
    const unsubscribeCOA = onSnapshot(coaQuery, (snapshot) => {
      setChartOfAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch organization users
    const usersQuery = query(
      collection(db, 'org_users')
    );
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter users who belong to this org
      const filteredUsers = allUsers.filter((u: any) => 
        u.memberships?.some((m: any) => m.orgId === orgId)
      );
      setOrgUsers(filteredUsers);
    });

    return () => {
      unsubscribeBank();
      unsubscribeCOA();
      unsubscribeUsers();
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/stores`, { headers: localApiHeaders() })
        .then(r => r.json())
        .then(data => {
          if (cancelled) return;
          setStores(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Stores load failed:', err);
          setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'stores'),
      where('orgId', '==', orgId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const storesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as StoreLocation[];
      
      setStores([...storesData].sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'stores');
      setLoading(false);
    });

    return unsubscribe;
  }, [orgId]);

  const syncStoresTrackingCategory = async (updatedStores?: StoreLocation[]) => {
    try {
      const storesToUse = updatedStores || stores;
      const activeStoreNames = storesToUse
        .filter(s => s.status === 'active')
        .map(s => s.name)
        .sort();

      if (isLocalSession()) {
        const cats = await fetch(`/api/organizations/${orgId}/tracking-categories`, { headers: localApiHeaders() }).then(r => r.json());
        const storesCategory = Array.isArray(cats) ? cats.find((c: any) => c.name === 'Stores') : null;
        if (storesCategory) {
          const currentOptions = [...(storesCategory.options || [])].sort();
          if (JSON.stringify(activeStoreNames) !== JSON.stringify(currentOptions)) {
            await fetch(`/api/organizations/${orgId}/tracking-categories/${storesCategory.id}`, {
              method: 'PATCH', headers: localApiHeaders(), body: JSON.stringify({ options: activeStoreNames }),
            });
          }
        } else {
          await fetch(`/api/organizations/${orgId}/tracking-categories`, {
            method: 'POST', headers: localApiHeaders(),
            body: JSON.stringify({ name: 'Stores', options: activeStoreNames }),
          });
        }
        return;
      }

      const q = query(
        collection(db, 'tracking_categories'),
        where('orgId', '==', orgId),
        where('name', '==', 'Stores')
      );
      
      const catSnapshot = await getDocs(q);
      if (!catSnapshot.empty) {
        const storesCategory = { id: catSnapshot.docs[0].id, ...catSnapshot.docs[0].data() } as any;
        const currentOptions = [...storesCategory.options].sort();
        
        if (JSON.stringify(activeStoreNames) !== JSON.stringify(currentOptions)) {
          await updateDoc(doc(db, 'tracking_categories', storesCategory.id), {
            options: activeStoreNames,
            updatedAt: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error('Error syncing stores tracking category:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      notify.error('Store name is required');
      return;
    }

    try {
      if (isLocalSession()) {
        const method = editingStore ? 'PATCH' : 'POST';
        const url = editingStore
          ? `/api/organizations/${orgId}/stores/${editingStore.id}`
          : `/api/organizations/${orgId}/stores`;
        const res = await fetch(url, { method, headers: localApiHeaders(), body: JSON.stringify({ ...formData, orgId }) });
        if (!res.ok) { const err = await res.json().catch(() => null); throw new Error(err?.error || 'Could not save store'); }
        const saved = await res.json() as StoreLocation;
        const updatedStores = editingStore
          ? stores.map(s => s.id === saved.id ? saved : s)
          : [...stores, saved].sort((a, b) => a.name.localeCompare(b.name));
        setStores(updatedStores);
        notify.confirm(editingStore ? 'Store updated successfully' : 'Store added successfully');
        await syncStoresTrackingCategory(updatedStores);
        setShowAddModal(false);
        setEditingStore(null);
        setFormData({ name: '', address: '', status: 'active', termsAndConditions: '', logoUrl: '', socialMediaLink: '', posPrefix: 'Receipt Number-', nextPosNumber: 1, enforceCustomerOnCheckout: false, allowedPaymentAccountIds: [], discountRules: [] });
        return;
      }

      if (editingStore) {
        const updatedStore = { ...editingStore, ...formData };
        await updateDoc(doc(db, 'stores', editingStore.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });
        notify.confirm('Store updated successfully');
        const updatedStores = stores.map(s => s.id === editingStore.id ? updatedStore : s);
        await syncStoresTrackingCategory(updatedStores);
      } else {
        const docRef = await addDoc(collection(db, 'stores'), {
          ...formData,
          orgId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        notify.confirm('Store added successfully');
        const newStore = { id: docRef.id, ...formData, orgId } as StoreLocation;
        await syncStoresTrackingCategory([...stores, newStore]);
      }
      
      setShowAddModal(false);
      setEditingStore(null);
      setFormData({ 
        name: '', 
        address: '', 
        status: 'active',
        termsAndConditions: '',
        logoUrl: '',
        socialMediaLink: '',
        posPrefix: 'Receipt Number-',
        nextPosNumber: 1,
        enforceCustomerOnCheckout: false,
        allowedPaymentAccountIds: [],
        discountRules: []
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'stores');
    }
  };

  const handleArchive = async (storeId: string) => {
    if (!window.confirm('Are you sure you want to archive this store? It cannot be deleted because it may contain sales data.')) return;

    const toastId = notify.loading('Archiving store...');
    try {
      if (isLocalSession()) {
        const res = await fetch(`/api/organizations/${orgId}/stores/${storeId}`, {
          method: 'PATCH', headers: localApiHeaders(), body: JSON.stringify({ status: 'archived' }),
        });
        if (!res.ok) throw new Error('Could not archive store');
        const updatedStores = stores.map(s => s.id === storeId ? { ...s, status: 'archived' as const } : s);
        setStores(updatedStores);
        notify.confirm('Store archived successfully', { id: toastId });
        await syncStoresTrackingCategory(updatedStores);
        return;
      }
      await updateDoc(doc(db, 'stores', storeId), {
        status: 'archived',
        updatedAt: serverTimestamp()
      });
      notify.confirm('Store archived successfully', { id: toastId });
      const updatedStores = stores.map(s => s.id === storeId ? { ...s, status: 'archived' as const } : s);
      await syncStoresTrackingCategory(updatedStores);
    } catch (error) {
      notify.error('Failed to archive store', { id: toastId });
      handleFirestoreError(error, OperationType.UPDATE, 'stores');
    }
  };

  const addDiscountRule = () => {
    setFormData(prev => ({
      ...prev,
      discountRules: [
        ...prev.discountRules,
        { userIds: [], minDiscount: 0, maxDiscount: 100 }
      ]
    }));
  };

  const removeDiscountRule = (index: number) => {
    setFormData(prev => ({
      ...prev,
      discountRules: prev.discountRules.filter((_, i) => i !== index)
    }));
  };

  const updateDiscountRule = (index: number, updates: Partial<DiscountRule>) => {
    setFormData(prev => ({
      ...prev,
      discountRules: prev.discountRules.map((rule, i) => 
        i === index ? { ...rule, ...updates } : rule
      )
    }));
  };

  const toggleUserInRule = (ruleIndex: number, userId: string) => {
    setFormData(prev => ({
      ...prev,
      discountRules: prev.discountRules.map((rule, i) => {
        if (i !== ruleIndex) return rule;
        const userIds = rule.userIds.includes(userId)
          ? rule.userIds.filter(id => id !== userId)
          : [...rule.userIds, userId];
        return { ...rule, userIds };
      })
    }));
  };

  const togglePaymentAccount = (accountId: string) => {
    setFormData(prev => {
      const current = prev.allowedPaymentAccountIds || [];
      if (current.includes(accountId)) {
        return { ...prev, allowedPaymentAccountIds: current.filter(id => id !== accountId) };
      } else {
        return { ...prev, allowedPaymentAccountIds: [...current, accountId] };
      }
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) { // 1MB limit for base64 in Firestore
      notify.error('Logo file size must be less than 1MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, logoUrl: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const filteredStores = stores.filter(store => 
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <Store className="w-10 h-10 text-primary" />
            Physical Stores
          </h1>
          <p className="text-gray-500 mt-2 text-lg">Manage your physical store locations and branches.</p>
        </div>
        <button
          onClick={() => {
            setEditingStore(null);
            setFormData({ 
              name: '', 
              address: '', 
              status: 'active',
              termsAndConditions: '',
              logoUrl: '',
              socialMediaLink: '',
              posPrefix: 'Receipt Number-',
              nextPosNumber: 1,
              enforceCustomerOnCheckout: false,
              allowedPaymentAccountIds: [],
              discountRules: []
            });
            setShowAdvanced(false);
            setShowDiscountSettings(false);
            setShowAddModal(true);
          }}
          className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Store
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary/20 transition-all"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="p-4 font-semibold text-gray-600 text-sm">Store Name</th>
                <th className="p-4 font-semibold text-gray-600 text-sm">Address</th>
                <th className="p-4 font-semibold text-gray-600 text-sm">Status</th>
                <th className="p-4 font-semibold text-gray-600 text-sm text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500">Loading stores...</td>
                </tr>
              ) : filteredStores.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500">
                    No stores found. Click "Add Store" to create one.
                  </td>
                </tr>
              ) : (
                filteredStores.map((store) => (
                  <tr key={store.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="p-4">
                      <div className="font-bold text-gray-900 flex items-center gap-2">
                        <Store className="w-4 h-4 text-gray-400" />
                        {store.name}
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {store.address || 'No address provided'}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                        store.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : store.status === 'archived'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {store.status === 'active' ? <CheckCircle2 className="w-3 h-3" /> : store.status === 'archived' ? <Archive className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {store.status.charAt(0).toUpperCase() + store.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setEditingStore(store);
                            setFormData({
                              name: store.name,
                              address: store.address || '',
                              status: store.status,
                              termsAndConditions: store.termsAndConditions || '',
                              logoUrl: store.logoUrl || '',
                              socialMediaLink: store.socialMediaLink || '',
                              posPrefix: store.posPrefix || 'Receipt Number-',
                              nextPosNumber: store.nextPosNumber || 1,
                              enforceCustomerOnCheckout: store.enforceCustomerOnCheckout || false,
                              allowedPaymentAccountIds: store.allowedPaymentAccountIds || [],
                              discountRules: store.discountRules || []
                            });
                            setShowAdvanced(false);
                            setShowDiscountSettings(false);
                            setShowAddModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {store.status !== 'archived' && (
                          <button
                            onClick={() => handleArchive(store.id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Archive Store"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowAddModal(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                {editingStore ? 'Edit Store' : 'Add New Store'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 gap-3">
                {formData.logoUrl ? (
                  <div className="relative group">
                    <img src={formData.logoUrl} alt="Store Logo" className="w-20 h-20 object-contain rounded-lg" referrerPolicy="no-referrer" />
                    <button 
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="store-logo-upload" className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      <Upload className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-bold text-gray-500">Upload Store Logo</p>
                  </label>
                )}
                <input
                  id="store-logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                {!formData.logoUrl && (
                  <label htmlFor="store-logo-upload" className="text-[10px] font-bold text-primary uppercase tracking-widest cursor-pointer hover:underline">Choose File</label>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Store Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="e.g. Downtown Branch"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  placeholder="Store physical address"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Social Media Link</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    value={formData.socialMediaLink}
                    onChange={(e) => setFormData({ ...formData, socialMediaLink: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="https://instagram.com/yourstore"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Terms and Conditions</label>
                <textarea
                  value={formData.termsAndConditions}
                  onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  placeholder="Terms to show at the bottom of the receipt"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'archived' })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">POS Prefix</label>
                  <input
                    type="text"
                    value={formData.posPrefix}
                    onChange={(e) => setFormData({ ...formData, posPrefix: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="Receipt Number-"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Next Number</label>
                  <input
                    type="number"
                    value={formData.nextPosNumber}
                    onChange={(e) => setFormData({ ...formData, nextPosNumber: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id="enforceCustomer"
                  checked={formData.enforceCustomerOnCheckout}
                  onChange={(e) => setFormData({ ...formData, enforceCustomerOnCheckout: e.target.checked })}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="enforceCustomer" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  Enforce Customer On Checkout
                </label>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Advanced Settings
                  </div>
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showAdvanced && (
                  <div className="mt-4 space-y-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100 animate-in slide-in-from-top-2 duration-200">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-3">
                        <CreditCard className="w-4 h-4 text-primary" />
                        Payment Channels
                      </label>
                      <p className="text-xs text-gray-500 mb-4">Select which bank accounts and payment-enabled accounts are available for this store.</p>
                      
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                        {/* Bank Accounts */}
                        {bankAccounts.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Bank Accounts</p>
                            {bankAccounts.map(account => (
                              <label key={account.id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer transition-colors group">
                                <input
                                  type="checkbox"
                                  checked={formData.allowedPaymentAccountIds.includes(account.id)}
                                  onChange={() => togglePaymentAccount(account.id)}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-700 group-hover:text-primary transition-colors">{account.accountName}</span>
                                  <span className="text-[10px] text-gray-500">{account.bankName} - {account.accountNumber}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}

                        {/* Chart of Accounts with enablePayments */}
                        {chartOfAccounts.filter(a => a.enablePayments).length > 0 && (
                          <div className="space-y-2 mt-4">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Other Payment Accounts</p>
                            {chartOfAccounts.filter(a => a.enablePayments).map(account => (
                              <label key={account.id} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer transition-colors group">
                                <input
                                  type="checkbox"
                                  checked={formData.allowedPaymentAccountIds.includes(account.id)}
                                  onChange={() => togglePaymentAccount(account.id)}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-700 group-hover:text-primary transition-colors">{account.name}</span>
                                  <span className="text-[10px] text-gray-500">{account.code} - {account.type}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}

                        {bankAccounts.length === 0 && chartOfAccounts.filter(a => a.enablePayments).length === 0 && (
                          <p className="text-sm text-gray-500 italic text-center py-4">No payment accounts found.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDiscountSettings(!showDiscountSettings)}
                  className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Discount Settings
                  </div>
                  {showDiscountSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showDiscountSettings && (
                  <div className="mt-4 space-y-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Percent className="w-4 h-4 text-primary" />
                        Discount Notification Management
                      </label>
                      <button
                        type="button"
                        onClick={addDiscountRule}
                        className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Rule
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Set discount limits for specific users. Users will be restricted to these values.</p>

                    <div className="space-y-4 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                      {formData.discountRules.map((rule, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm space-y-4 relative group">
                          <button
                            type="button"
                            onClick={() => removeDiscountRule(idx)}
                            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Min Discount (%)</label>
                              <input
                                type="number"
                                value={isNaN(rule.minDiscount) ? '' : rule.minDiscount}
                                onChange={(e) => updateDiscountRule(idx, { minDiscount: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Max Discount (%)</label>
                              <input
                                type="number"
                                value={isNaN(rule.maxDiscount) ? '' : rule.maxDiscount}
                                onChange={(e) => updateDiscountRule(idx, { maxDiscount: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                                min="0"
                                max="100"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Restricted Users</label>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {rule.userIds.map(uid => {
                                const user = orgUsers.find(u => u.id === uid);
                                return (
                                  <span key={uid} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-[10px] font-bold rounded-full">
                                    {user?.fullName || 'Unknown'}
                                    <button type="button" onClick={() => toggleUserInRule(idx, uid)}>
                                      <XCircle className="w-3 h-3" />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                            <select
                              className="w-full px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                              onChange={(e) => {
                                if (e.target.value) {
                                  toggleUserInRule(idx, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">Select user to add...</option>
                              {orgUsers.filter(u => !rule.userIds.includes(u.id)).map(user => (
                                <option key={user.id} value={user.id}>{user.fullName}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                      {formData.discountRules.length === 0 && (
                        <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-2xl">
                          <Percent className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">No discount rules defined.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 text-gray-600 font-semibold hover:bg-gray-50 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg shadow-primary/20"
                >
                  {editingStore ? 'Save Changes' : 'Add Store'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
